import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, ReplaySubject, Subject, take } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { isInDev } from 'app/core/utilities/env-util';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { Interval, Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import SocialSettings from 'entity/SocialSettings';
import User from 'entity/User';
import {
  IncomingMessage,
  PeerCandidate,
  OutgoingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  SessionMessage,
  TypedOutgoingMessage,
  VersionCheckMessage,
  ClientType,
  WEBSOCKET_MESSAGE_VERSION,
  VersionCheckedMessage,
  StartedMessage,
} from 'messages/websocket.messages';

import { DEBUGGER_MESSAGE_HISTORY_LENGTH } from '../constants';
import { actions } from '../state/app';
import { mediaSelectors } from '../state/media';
import { BrowserService } from './browser.service';

type WebsocketMessage =
  | IncomingMessage
  | OutgoingMessage
  | RTCIceCandidate
  | RTCSessionDescription
  | TypedOutgoingMessage;

const WEBSOCKET_PING_INTERVAL_PERIOD = 60 * 1000;
const ROOM_JOIN_TIMEOUT_MS = 3 * 60 * 1000;
const WEBSOCKET_CLOSE_TIMEOUT = 3 * 1000;

export enum WebsocketDisconnectType {
  UNKNOWN = 'UNKNOWN',
  CLOSED = 'CLOSED',
  INCOMPATIBLE_CLIENT = 'INCOMPATIBLE_CLIENT',
  WEBSOCKET_ERROR = 'WEBSOCKET_ERROR',
}

export interface DisconnectStatus {
  type: WebsocketDisconnectType;
  error: boolean;
}

export enum WebsocketEventCode {
  ABNORMAL_CLOSURE = 1006,
  NO_STATUS_RECEIVED = 1005,
}

export enum WebsocketStateType {
  CONNECTED = 'connected',
  CONNECTING = 'connecting',
  DISCONNECTED = 'disconnected',
  FAILED = 'failed',
  CLOSING = 'closing',
}

export interface MessageRecord {
  direction: 'in' | 'out';
  time: string;
  msg: WebsocketMessage;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService extends Loggable {
  private store = inject(Store);
  private dialog = inject(MatDialog);
  private browserService = inject(BrowserService);

  private peerCandidateSubject = new Subject<PeerCandidate>();
  private messageSubject = new Subject<IncomingMessage>();
  private _messageHistory: MessageRecord[] = [];
  private messageHistorySubject = new ReplaySubject<MessageRecord>(DEBUGGER_MESSAGE_HISTORY_LENGTH);
  private disconnectSubject = new Subject<DisconnectStatus>();
  private stateSubject = new BehaviorSubject<WebsocketStateType>(WebsocketStateType.DISCONNECTED);
  private openSubject = new BehaviorSubject<boolean>(false);

  private isVideoEnabled = true;
  private isMicEnabled = true;
  private socket?: WebSocket;
  private pingInterval?: Interval;
  private connectionTimeout?: Timeout;
  private connectionResolve?: () => void;
  private connectionCloseResolve?: () => void;
  private connectionCloseTimeout?: Timeout;

  public message$ = this.messageSubject.asObservable();
  public messageHistory$ = this.messageHistorySubject.asObservable();
  public peerCandidate$ = this.peerCandidateSubject.asObservable();
  public disconnect$ = this.disconnectSubject.asObservable();
  public state$ = this.stateSubject.asObservable();
  public open$ = this.openSubject.asObservable();
  private _url?: string;
  private _state: WebsocketStateType = WebsocketStateType.DISCONNECTED;
  private _videoServer: Record<string, unknown> = {};
  private ringColor?: string;

  private reconnect = false;
  private user?: User;
  public get state(): WebsocketStateType {
    return this._state;
  }
  private setState(newState: WebsocketStateType) {
    this.info(`websocket state change ${this._state} -> ${newState}`, { newState });
    this._state = newState;
    this.stateSubject.next(newState);
  }

  private isDebuggerEnabled = false;
  private isFailFastEnabled = false;
  private worker?: Worker;
  private destroyed$ = new ReplaySubject<boolean>(1);

  // Reconnection properties
  private reconnectAttemptCount = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeoutMs = 2000;
  private reconnectTimeout?: Timeout;

  public constructor() {
    super();
    this.setDisplayName('WebsocketService');
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user?: User) => {
        this.user = user;
      });
    this.store.select(mediaSelectors.isVideoEnabled).subscribe((enabled) => (this.isVideoEnabled = enabled));
    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_debugger)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isDebuggerEnabled) => (this.isDebuggerEnabled = isDebuggerEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_failfast_logic)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((failFast) => (this.isFailFastEnabled = failFast));
  }

  public connect(url: string): void {
    this._url = url;
    this.socket = new WebSocket(url);
    this.socket.onmessage = this.onMessage;
    this.socket.onopen = this.onOpen;
    this.socket.onerror = this.onError;
    this.socket.onclose = this.onClose;

    if (isInDev()) {
      window._socket = this.socket;
    }

    this.setState(WebsocketStateType.CONNECTING);
    if (typeof Worker !== 'undefined') {
      // https://preciate.atlassian.net/browse/WEB-1899
      // FIXME: We are using a file from outside the bundle for the web worker to get around webpack mis-bundling the
      // worker when included in Angular bundle. The chunk hash would change each build, and would not match the
      // resulting file. This started happening with the move to Angular 12 and webpack 5. This is a temporary fix, and
      // it would be better to switch back to bundling the worker later.
      this.worker = new Worker('/worker/websocket.worker.js');
      this.worker.onmessage = ({ data }) => {
        if (data === 'ping') {
          this.sendPing();
        }
      };
      this.worker.postMessage({ interval: WEBSOCKET_PING_INTERVAL_PERIOD });
    } else {
      this.warn(
        'Web Workers are not supported within this environment, using fallback (This can cause music issues during presentation).',
      );
      this.pingInterval = setInterval(this.sendPing, WEBSOCKET_PING_INTERVAL_PERIOD);
    }
  }

  public close(): Promise<void> {
    this.setState(WebsocketStateType.CLOSING);
    return new Promise((resolve) => {
      if (!this.socket) {
        resolve();
        return;
      }
      try {
        this.socket.send('leave');
        this.socket.close();
        this.socket = undefined;
      } catch (e) {
        this.error('Failed to close socket', e);
        resolve();
        return;
      }
      this.connectionCloseResolve = resolve;
      this.connectionCloseTimeout = setTimeout(() => {
        this.warn('Close timeout reached, resolving');
        resolve();
      }, WEBSOCKET_CLOSE_TIMEOUT);
    });
  }

  public sendMessageAndAwaitResponse(
    msg: OutgoingMessage,
    isValidResponse: (msg: IncomingMessage) => boolean,
  ): Observable<IncomingMessage> {
    this.sendMessage(msg);
    return this.message$.pipe(
      filter((incomingMsg) => isValidResponse(incomingMsg)),
      take(1),
    );
  }

  /**
   *
   * @param msg
   * @param options Options object
   * @param options.force Boolean if true, ignore the websocket state and send the message
   * @returns
   */
  public sendMessage(
    msg: OutgoingMessage | RTCIceCandidate | RTCSessionDescription | TypedOutgoingMessage,
    options?: { force: boolean },
  ): void {
    const { force } = options ?? {};
    this.recordMessage('out', msg);
    let encoded = '';
    if (!this.socket) {
      this.error('Attempted to send message while socket was not open', msg);
      const ev = new Event('Attempted to send message while socket was not open');
      this.onError(ev);
      // Try to reconnect when the socket is unavailable
      this.attemptReconnect();
      return;
    }
    if (!force && this._state !== WebsocketStateType.CONNECTED) {
      this.error('Attempted to send message while socket is not connected', msg);
      return;
    }
    try {
      encoded = JSON.stringify(msg);
    } catch (e) {
      this.error('Failed to encode message', e);
      throw e;
    }

    this.socket.send(encoded);
  }

  /**
   * @deprecated Use sendMessage with a properly typed message
   */
  public sendRaw(msg: string): void {
    if (!this.socket) {
      this.error('Attempted to send message while socket was not open', msg);
      return;
    }

    this.socket.send(msg);
  }

  public get isConnected(): boolean {
    return this.state === WebsocketStateType.CONNECTED;
  }

  /**
   * Get the websocket url
   */
  public get url(): string | undefined {
    return this._url;
  }

  public joinRoom(ringColor?: string): Promise<void> {
    this.info('Sending posreq and musinf messages...');
    this.socket?.send('posreq');
    this.socket?.send('musinf');
    this.ringColor = ringColor;

    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject('Timeout waiting for join response');
      }, ROOM_JOIN_TIMEOUT_MS);

      this.message$
        .pipe(
          filter(
            ({ t: type }) =>
              type === IncomingMessageType.SESSION_TIMEOUT || type === IncomingMessageType.SESSION_CREATED,
          ),
          take(1),
          takeUntil(this.destroyed$),
        )
        .subscribe((message) => {
          const { t: type } = message;
          switch (type) {
            case IncomingMessageType.SESSION_CREATED:
              this.info('Received session created message', message);
              this._videoServer = {
                mixStreamId: message.mixStreamId,
                videoServer: message.videoServer,
                videoServerIndex: message.videoServerIndex,
              };
              clearTimeout(timeout);
              resolve();
              break;
            case IncomingMessageType.SESSION_TIMEOUT:
              this._videoServer = {};
              clearTimeout(timeout);
              reject('Received Session Timeout');
              break;
          }
        });
    });
  }

  private onMessage = (event: MessageEvent) => {
    const { data } = event;

    if (data === 'pong' || data === '{}') {
      return;
    }

    let untypedJSON;
    try {
      untypedJSON = JSON.parse(data);
    } catch (e) {
      try {
        this.error('Error parsing JSON: ', btoa(unescape(encodeURIComponent(data))));
      } catch (err) {
        this.error('Error parsing JSON, and failed to base64 offending data', err);
      }
      throw e;
    }

    if (untypedJSON.candidate) {
      this.peerCandidateSubject.next(untypedJSON as PeerCandidate);
      return;
    }
    const { t: type } = untypedJSON;
    if (typeof type !== 'string') {
      this.error('Unexpected type identifier in websocket message JSON', { msg: untypedJSON });
      return;
    }
    const msg = untypedJSON as IncomingMessage;
    this.messageSubject.next(msg);

    // Moved messages are very noisy, skip them in history
    if (msg.t !== IncomingMessageType.MOVED) {
      this.recordMessage('in', msg);
    }

    // Initial position indicates that the connection is complete.

    switch (type) {
      case IncomingMessageType.SERVER_ERROR: {
        if (this.isFailFastEnabled) {
          this.throwFailFastModal(origin, untypedJSON);
        }
        this.error('Server error', untypedJSON);
        break;
      }

      case IncomingMessageType.STATE_NOT_CHANGED: {
        this.error('state not changed', untypedJSON);
        break;
      }

      case IncomingMessageType.VERSION_CHECKED: {
        this.handleVersionChecked(msg as VersionCheckedMessage);
        break;
      }

      case IncomingMessageType.SETTINGS_CHANGED: {
        const settings = untypedJSON.settings ?? {};
        this.handleSettingsChanged(settings);
        break;
      }

      case IncomingMessageType.INITPOS: {
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
        }
        this.setState(WebsocketStateType.CONNECTED);
        this.connectionResolve?.();

        if (!this.user) {
          this.fatal('Connected, but session user was not set');
          return;
        }
        const isMobile = this.browserService.isMobile();
        const session: SessionMessage = {
          t: OutgoingMessageType.SESSION,
          user: { userId: '', ...this.user, ringColor: this.ringColor, mobile: isMobile },
          reconnect: this.reconnect,
          camera: this.isVideoEnabled,
          mic: this.isMicEnabled,
          publishPeerTime: 0,
        };
        this.reconnect = true;
        this.sendMessage(session);
        break;
      }
      case IncomingMessageType.STARTED: {
        void this.handleRoomStarted(msg as StartedMessage);
        return;
      }

      case IncomingMessageType.SESSION_CREATED: {
        // this.churnZeroService.trackEvent(ChurnZeroEvent.ACTIVE_SESSION);
        return;
      }
    }
  };

  /**
   * Websocket open handler
   */
  private onOpen = () => {
    this.info('Signalling websocket opened', { url: this.url });
    if (!this.socket) {
      this.fatal('Websocket opened, but socket member is undefined');
      return;
    }

    const versionCheck: VersionCheckMessage = {
      t: OutgoingMessageType.VERSION_CHECK,
      clientType: ClientType.WEB_APP,
      version: WEBSOCKET_MESSAGE_VERSION,
    };
    this.sendMessage(versionCheck, { force: true });

    this.openSubject.next(true);

    // Reset reconnection counters on successful open
    this.onSuccessfulConnection();
  };

  /**
   * Websocket error handler
   */
  private onError = (ev: Event) => {
    this.socket = undefined;
    this.setState(WebsocketStateType.FAILED);

    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    this.worker?.terminate();

    this.error('Websocket failed with error', ev);
    this.disconnectSubject.next({ error: true, type: WebsocketDisconnectType.WEBSOCKET_ERROR });

    // Try to reconnect when an error occurs
    this.attemptReconnect();
  };

  /**
   * Websocket close handler
   */
  private onClose = (ev: CloseEvent) => {
    this.socket = undefined;
    this.setState(WebsocketStateType.DISCONNECTED);
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    this.worker?.terminate();
    this.openSubject.next(false);
    this.connectionCloseResolve?.();
    if (this.connectionCloseTimeout) {
      clearTimeout(this.connectionCloseTimeout);
      this.connectionCloseTimeout = undefined;
    }

    this.info(`Audio Web Socket closed with code ${ev.code}`);
    // FIXME: We may want to return error: false directly without checking at the event code, right now
    // that is not possible because of the way the websocket closes when it got disconnected.
    this.disconnectSubject.next({
      error: ev.code === WebsocketEventCode.ABNORMAL_CLOSURE,
      type: WebsocketDisconnectType.CLOSED,
    });
  };

  private sendPing = (): void => this.socket?.send('ping');

  private handleVersionChecked(msg: VersionCheckedMessage) {
    const { compatible } = msg;
    if (compatible) {
      return;
    }

    this.error('Client version is not compatible with server', msg);

    this.disconnectSubject.next({ error: true, type: WebsocketDisconnectType.INCOMPATIBLE_CLIENT });
    this.socket?.close();
    this.worker?.terminate();
  }

  public destroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();

    if (this.connectionCloseTimeout) {
      clearTimeout(this.connectionCloseTimeout);
      this.connectionCloseTimeout = undefined;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
  }

  public get videoServer(): Record<string, unknown> {
    return this._videoServer;
  }

  private handleSettingsChanged(settings: SocialSettings): void {
    if (settings) {
      this.store.dispatch(actions.SET_SOCIAL_SETTINGS({ settings }));
    }
  }

  /**
   * Attempts to reconnect to the websocket server after a disconnection
   * Uses exponential backoff strategy for retries
   * this *should* be redundant to existing health checks
   */
  private attemptReconnect(): void {
    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }

    // Only attempt reconnection if we have a URL and haven't exceeded max attempts
    if (!this._url || this.reconnectAttemptCount >= this.maxReconnectAttempts) {
      if (this.reconnectAttemptCount >= this.maxReconnectAttempts) {
        this.error('Max reconnection attempts reached');
      }
      return;
    }

    this.info(`Attempting to reconnect (attempt ${this.reconnectAttemptCount + 1}/${this.maxReconnectAttempts})...`);

    // Use exponential backoff for reconnect timeout (2s, 4s, 8s, 16s, 32s)
    const timeout = this.reconnectTimeoutMs * Math.pow(2, this.reconnectAttemptCount);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttemptCount++;

      // Only reconnect if we're not already connecting or connected
      if (this._state !== WebsocketStateType.CONNECTING && this._state !== WebsocketStateType.CONNECTED) {
        this.info(`Reconnecting to ${this._url}`);
        // Set reconnect flag to true before connecting
        this.reconnect = true;
        this.connect(this._url!);
      }
    }, timeout);
  }

  /**
   * Reset reconnect counter when successfully connected
   */
  private onSuccessfulConnection(): void {
    this.reconnectAttemptCount = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
  }

  /**
   * Handle room started
   *
   * This indicates a closed room has now been opened. The room may be on a different server than the one connected to.
   *  If so, connect to the new server.
   *
   * @param msg: StartedMessage
   */
  private async handleRoomStarted(msg: StartedMessage): Promise<void> {
    const { currentServer, websocketUrl } = msg;
    this.store.dispatch(actions.ROOM_STARTED({ websocketUrl }));
    if (!currentServer) {
      this.info('Server has changed, using new url for websocket connection', { msg });
      try {
        await this.close();
        this.connect(websocketUrl);
      } catch (error) {
        this.error('Error after trying to refresh websocket connection.', error);
      }
    }
  }

  // #region Test Utilities
  private recordMessage(direction: 'in' | 'out', msg: WebsocketMessage): void {
    const record = { direction, msg, time: new Date().toISOString() };
    if (this.isDebuggerEnabled) {
      this.messageHistorySubject.next(record);
    }
    this._messageHistory.push(record);
    if (this._messageHistory.length > DEBUGGER_MESSAGE_HISTORY_LENGTH) {
      this._messageHistory.pop();
    }
  }

  /**
   * FailFast Error Modal
   */

  private throwFailFastModal(origin: string, d: JSON): void {
    this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
      data: {
        title: `Server Error: ${origin}`,
        json: d,
        isJson: true,
      },
    });
  }

  /**
   * Simulate websocket failure
   */
  public simulateFailure(): void {
    this.socket?.close();
    const ev = new Event('simulated failure');
    this.onError(ev);
  }

  /**
   * Simulate websocket close
   */
  public simulateClose(): void {
    this.socket?.close();
    const ev = new CloseEvent('simulated close', { code: WebsocketEventCode.ABNORMAL_CLOSURE });
    this.onError(ev);
  }

  /**
   * Simulate incompatible client
   */
  public simulateIncompatibleVersion(): void {
    const msg: VersionCheckedMessage = {
      compatible: false,
      t: IncomingMessageType.VERSION_CHECKED,
      minVersion: WEBSOCKET_MESSAGE_VERSION,
    };
    this.handleVersionChecked(msg);
  }

  /**
   * Simulate reception of a message
   * @param msg The message to simulate.
   */
  public simulateMessage(msg: IncomingMessage) {
    this.info('Simulated message', msg);
    this.messageSubject.next(msg);
  }

  public get messageHistory(): MessageRecord[] {
    return [...this._messageHistory];
  }

  // #endregion
}
